import { HttpErrorResponse } from '@angular/common/http'
import {
  Order,
  OrderAdditionalPackage,
  OrderPackage,
  OrderPackageType,
  OrderPackagesData,
} from '../models/order.entity'
import { computed, Injectable, signal } from '@angular/core'
import { OrdersRepository } from '../orders.repository'
import {
  Product,
  ProductStatus,
  ProductWrapper,
  getProductWrapperByBarcode,
} from '../../products'
import {
  addOrderAdditionalPackage,
  addOrderPackage,
  addOrderProductAdditionalPackage,
  addOrderProductPackage,
  addOrderWrapperAdditionalPackage,
  addOrderWrapperPackage,
  getOrderPackagesVolume,
  getOrderPackagesWeight,
  removeOrderAdditionalPackage,
  removeOrderPackage,
  updateOrderPackageVolume,
} from '../libs/order-packages.lib'
import { uniqBy } from 'lodash'
import { OrdersService } from '../orders.service'

interface OrderPackagesState {
  orderId?: string
  packages?: OrderPackage[]
  additionalPackages?: OrderAdditionalPackage[]
  products?: Product[]
  error?: HttpErrorResponse
  packageType: OrderPackageType
  weightToPack: number
  isLoading: boolean
  isModified: boolean
}

const ORDER_PACKAGES_INITIAL_STATE: OrderPackagesState = {
  packageType: 'package',
  weightToPack: 0,
  isLoading: false,
  isModified: false,
}

@Injectable()
export class OrderPackagesStore {
  constructor(
    private ordersRepository: OrdersRepository,
    private ordersService: OrdersService,
  ) {}

  private state = signal<OrderPackagesState>(ORDER_PACKAGES_INITIAL_STATE)

  // Selectors
  readonly packages = computed(() => this.state().packages)
  readonly packageType = computed(() => this.state().packageType)
  readonly additionalPackages = computed(() => this.state().additionalPackages)
  readonly products = computed(() => this.state().products)
  readonly volume = computed(() =>
    getOrderPackagesVolume(this.packages() || []),
  )
  readonly weight = computed(() =>
    getOrderPackagesWeight(this.packages() || []),
  )
  readonly weightToPack = computed(() => this.state().weightToPack)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(orderId?: string): Promise<void> {
    if (!orderId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.read$(orderId).subscribe({
        next: async (data) => {
          this.ordersRepository
            .getOrderPackagesData$(data, this.products())
            .subscribe((packagesData) => {
              this.setData(
                packagesData,
                orderId,
                this.state().orderId ? this.state().orderId !== orderId : false,
              )
              resolve()
            })
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async searchPackage(code: string): Promise<void> {
    if (!code) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersRepository.getProductPackage$(code).subscribe({
        next: async (data) => {
          if (!data) {
            this.ordersRepository.notify({ code: 'PACKAGE_NOT_FOUND' })
            resolve()
            return
          }
          this.parseProduct(data, code)
          resolve()
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async editPackages(order?: Order): Promise<void> {
    if (!order) {
      return
    }
    this.unsetError()
    this.setOrderId(order._id)
    const products = this.products()

    return new Promise((resolve, reject) => {
      this.ordersRepository.getOrderPackagesData$(order, products).subscribe({
        next: (data) => {
          if (!data) {
            this.ordersRepository.notify({ code: 'CHANGES_NOT_APPLIED' })
            resolve()
            return
          }
          this.setData(data, order._id)
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  // Utilities

  private parseProduct(productPackage: Product, code: string) {
    // Check wrapper
    const wrapper = getProductWrapperByBarcode(productPackage, code)
    if (wrapper?.isSelfShipping) {
      this.addProductWrapper(wrapper)
      return
    }

    // Check type
    if (productPackage.productType !== 'PACKAGE') {
      this.ordersRepository.notify({ code: 'PACKAGE_NOT_AVAILABLE' })
      return
    }

    // Check status
    if (productPackage.status === ProductStatus.disabled) {
      this.ordersRepository.notify({ code: 'PACKAGE_DISABLED' })
      return
    }

    this.addProductPackage(productPackage)
  }

  // Reducers

  setData(
    packagesData: OrderPackagesData,
    orderId: string,
    isModified?: boolean,
  ) {
    this.state.set({
      ...this.state(),
      orderId: orderId,
      isModified: isModified ?? this.state().isModified,
      packages: packagesData.packages,
      additionalPackages: packagesData.additionalPackages,
      products: packagesData.products,
      isLoading: false,
      error: undefined,
    })
  }

  setOrderId(orderId: string) {
    this.state.set({
      ...this.state(),
      orderId: orderId,
    })
  }

  setPackageType(packageType: OrderPackageType) {
    this.state.set({
      ...this.state(),
      packageType,
      isLoading: false,
      error: undefined,
    })
  }

  addProductWrapper(wrapper: ProductWrapper) {
    const packageType = this.packageType()
    const packages = this.packages()
    const additionalPackages = this.additionalPackages()
    const weightToPack = this.weightToPack()

    this.ordersRepository.notify({ code: 'PACKAGE_ADDED' })

    this.state.set({
      ...this.state(),
      packages:
        packageType === 'package'
          ? addOrderWrapperPackage(packages || [], wrapper, weightToPack)
          : packages,
      additionalPackages:
        packageType === 'additionalPackage'
          ? addOrderWrapperAdditionalPackage(
              additionalPackages || [],
              wrapper,
              weightToPack,
            )
          : additionalPackages,
      weightToPack: 0,
      isModified: true,
      isLoading: false,
    })
  }

  addProductPackage(productPackage: Product) {
    const packageType = this.packageType()
    const packages = this.packages()
    const additionalPackages = this.additionalPackages()
    const weightToPack = this.weightToPack()
    const products = this.products()

    this.ordersRepository.notify({ code: 'PACKAGE_ADDED' })

    this.state.set({
      ...this.state(),
      packages:
        packageType === 'package'
          ? addOrderProductPackage(packages || [], productPackage, weightToPack)
          : packages,
      additionalPackages:
        packageType === 'additionalPackage'
          ? addOrderProductAdditionalPackage(
              additionalPackages || [],
              productPackage,
              weightToPack,
            )
          : additionalPackages,
      weightToPack: 0,
      products: uniqBy([...(products || []), productPackage], '_id'),
      isLoading: false,
      isModified: true,
    })
  }

  addPackage(packData: { additional?: boolean }) {
    const packages = this.packages()
    const additionalPackages = this.additionalPackages()
    const weightToPack = this.weightToPack()

    this.state.set({
      ...this.state(),
      packages: !packData.additional
        ? addOrderPackage(packages || [], {
            weight: weightToPack,
          })
        : packages,
      additionalPackages: packData.additional
        ? addOrderAdditionalPackage(additionalPackages || [], {
            weight: weightToPack,
          })
        : additionalPackages,
      weightToPack: 0,
      isModified: true,
    })
  }

  removePackage(packData: { additional?: boolean; index?: number }) {
    const packages = this.packages()
    const additionalPackages = this.additionalPackages()
    const weightToPack = this.weightToPack()

    const packs = packData.additional ? additionalPackages : packages
    if (!packs?.length) {
      return
    }

    const indexToRemove =
      packData.index !== undefined ? packData.index : packs.length - 1
    const weightToRestore = packs[indexToRemove].weight

    this.state.set({
      ...this.state(),
      packages: !packData.additional
        ? removeOrderPackage(packages!, indexToRemove)
        : packages,
      additionalPackages: packData.additional
        ? removeOrderAdditionalPackage(additionalPackages!, indexToRemove)
        : additionalPackages,
      weightToPack: weightToPack + weightToRestore,
      isModified: true,
    })
  }

  updatePackage(packData: {
    package: OrderPackage | OrderAdditionalPackage
    additional: boolean
    index: number
  }) {
    const packages = this.packages()
    const additionalPackages = this.additionalPackages()

    this.state.set({
      ...this.state(),
      packages: !packData.additional
        ? packages?.map((p, i) =>
            i === packData.index
              ? updateOrderPackageVolume(packData.package)
              : p,
          )
        : packages,
      additionalPackages: packData.additional
        ? additionalPackages?.map((p, i) =>
            i === packData.index
              ? updateOrderPackageVolume(
                  packData.package as OrderAdditionalPackage,
                )
              : p,
          )
        : additionalPackages,
      isModified: true,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
